import React from 'react'
import {
  IconPencil,
  IconCloud,
  IconLockOpen,
  IconClipboard,
  IconUserPlus,
  IconUserMinus,
  IconMessageCircle2,
  IconCheck,
  IconReload,
  IconLockCog,
  IconReplace,
  IconLifebuoy,
} from '@tabler/icons-react'

import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'

const buildBaseActions = (row, openRowModal) => {
  const actions = [
    {
      label: `Edit ${row.full_name}`,
      icon: <IconPencil />,
      onClick: () => openRowModal('editUser', row),
      testId: `edit-user-${row.id}`,
      disabled: row.deleted,
    },
    {
      label: 'Send Password Reset Email',
      icon: <IconCloud />,
      onClick: () => openRowModal('resetPassword', row),
      testId: `reset-password-${row.id}`,
      disabled: row.deleted,
    },
  ]

  if (row.mfa_type) {
    actions.push({
      label: 'Reset MFA Config',
      icon: <IconLockCog />,
      onClick: () => openRowModal('resetMfa', row),
      testId: `reset-mfa-${row.id}`,
      disabled: row.deleted,
    })
  }

  actions.push({
    label: 'Change History',
    icon: <IconReplace />,
    onClick: () => openRowModal('changeHistory', row),
    testId: `change-history-${row.id}`,
    hasSeparator: true,
  })

  return actions
}

const buildAdminActions = (
  row,
  { qaEnabled, qaCopilotEnabled, currentUserEmail, openRowModal }
) => {
  const isOrgOne = row.organization_id === 1
  const actions = [
    {
      label: `${row.edit_config ? 'Revoke' : 'Grant'} Playbook Management`,
      icon: <IconClipboard />,
      onClick: () => openRowModal('playbookManagement', row),
      testId: `playbook-management-${row.id}`,
      disabled: row.deleted || isOrgOne,
      hasSeparator: true,
    },
    {
      label: `${row.edit_users ? 'Revoke' : 'Grant'} User Management`,
      icon: row.edit_users ? <IconUserMinus /> : <IconUserPlus />,
      onClick: () => openRowModal('userManagement', row),
      testId: `user-management-${row.id}`,
      disabled: row.deleted || isOrgOne || currentUserEmail === row.username,
    },
  ]

  if (qaEnabled) {
    actions.push({
      label: `${row.edit_qa ? 'Revoke' : 'Grant'} QA Access`,
      icon: <IconMessageCircle2 />,
      onClick: () => openRowModal('qaManagement', row),
      testId: `qa-management-${row.id}`,
      disabled: row.deleted || isOrgOne,
    })
  }

  if (qaCopilotEnabled) {
    actions.push({
      label: `${row.edit_qa_copilot ? 'Revoke' : 'Grant'} Quality Access`,
      icon: <IconCheck />,
      onClick: () => openRowModal('qaCopilotManagement', row),
      testId: `qa-copilot-management-${row.id}`,
      disabled: row.deleted || isOrgOne,
    })
  }

  return actions
}

export const buildDropdownActions = (row, context) => {
  const { isAdmin, editUsers, userFilter, openRowModal, restoreUser } = context
  const menuActions = [...buildBaseActions(row, openRowModal)]

  if (isAdmin) {
    menuActions.push({
      label: row.password ? 'Reset Password' : 'Add Password',
      icon: <IconLockOpen />,
      onClick: () => openRowModal('generatePassword', row),
      testId: `generate-password-${row.id}`,
      disabled: row.deleted,
    })
  }

  if (isAdmin && editUsers) {
    menuActions.push(...buildAdminActions(row, context))
  }

  if ((userFilter === 'deleted' || userFilter === 'all') && row.deleted) {
    menuActions.push({
      label: 'Restore User',
      icon: <IconReload />,
      onClick: () => restoreUser(row.id),
      testId: `restore-user-${row.id}`,
      hasSeparator: true,
    })
  }

  // Last on purpose: support actions reach into the user's desktop app, so they sit apart
  // from the account changes above
  if (isAdmin) {
    menuActions.push({
      label: 'Support Actions',
      icon: <IconLifebuoy />,
      onClick: () => openRowModal('supportActions', row),
      testId: `support-actions-${row.id}`,
      // Deactivated users can't be signed in to the desktop app
      disabled: row.deleted,
      hasSeparator: true,
    })
  }

  return menuActions
}

export const buildActionsForUser = ({ isAdmin, editUsers, actionContext }) => {
  if (!(isAdmin || editUsers)) {
    return []
  }

  return [
    {
      label: 'Actions',
      trigger: (row) => {
        return (
          <ActionsDropdown
            actions={buildDropdownActions(row, actionContext)}
            dataTestId={`user-actions-${row.id}`}
          />
        )
      },
    },
  ]
}
